<script setup lang="ts">
import { localToday } from "@/utils/localDate";
import { computed, onMounted, onUnmounted, ref } from "vue";
import type { OutboxCounts, OutboxEntry } from "@/offline/outbox";
import {
  countByStatus,
  exportUnsent,
  importUnsent,
  listEntries,
  updateEntry,
} from "@/offline/outbox";
import { drainOutbox, retryEntry, subscribeSyncEvents } from "@/offline/syncEngine";
import useOfflineStore from "@/stores/offline.store";
import useToastify from "@/stores/toastify.store";
import { canPage } from "@/helper/permissionHelper";

/**
 * What this device still owes the server.
 *
 * Reached from the sync indicator rather than the menu: it is a troubleshooting
 * screen, not part of anyone's daily round. It answers one question — "did my
 * sales arrive?" — and gives a manager the two actions that matter: send it
 * again, or take it off this device.
 */
definePage({
  meta: {
    // Anyone who can sell can see what their till has not yet sent.
    permissionKey: "page.pos.sell",
  },
});

const { t, d, n } = useI18n({ useScope: "global" });
const toast = useToastify();
const offline = useOfflineStore();

const entries = ref<OutboxEntry[]>([]);
const counts = ref<OutboxCounts>({ pending: 0, syncing: 0, needsAttention: 0, conflict: 0, synced: 0, unsent: 0 });
const loading = ref(false);
const syncing = ref(false);
const filter = ref<"unsent" | "needs_attention" | "synced" | "all">("unsent");

/**
 * Throwing work away is destructive and unrecoverable, so it is gated on the
 * cashier-admin page rather than on being able to sell.
 */
const canDiscard = computed(() => canPage("page.accounting.cashiers"));

const filtered = computed(() => {
  if (filter.value === "all")
    return entries.value;

  if (filter.value === "unsent")
    return entries.value.filter(row => row.status === "pending" || row.status === "syncing");

  if (filter.value === "needs_attention")
    return entries.value.filter(row => row.status === "needs_attention" || row.status === "conflict");

  return entries.value.filter(row => row.status === "synced");
});

const statusColor: Record<string, string> = {
  pending: "warning",
  syncing: "info",
  synced: "success",
  needs_attention: "error",
  conflict: "error",
  discarded: "secondary",
};

async function refresh() {
  loading.value = true;

  try {
    const [rows, totals] = await Promise.all([listEntries(), countByStatus()]);

    // Newest first: a queue screen is read top-down when something looks wrong.
    entries.value = rows.reverse();
    counts.value = totals;
  }
  catch (error: any) {
    toast.error({ text: error?.message ?? t("alkokh.offline.queueUnavailable") });
  }
  finally {
    loading.value = false;
  }
}

async function syncNow() {
  syncing.value = true;

  try {
    const outcome = await drainOutbox();

    // A deployment problem, not a sale problem: every queued sale fails identically until
    // the site runs the offline patch, so say that once rather than flag each sale.
    if (outcome.notInstalled) {
      toast.error({ text: t("alkokh.offline.notInstalled") });
    }
    else if (outcome.stopped === "offline") {
      toast.warning({ text: t("alkokh.offline.banner") });
    }
    else if (outcome.synced) {
      toast.success({ text: t("alkokh.offline.syncedCount", { count: outcome.synced }, outcome.synced) });

      // The sale went through, but it is in the books on today's date rather than the day
      // it happened. The till is the only place anyone will notice in time to act.
      if (outcome.backdated)
        toast.warning({ text: t("alkokh.offline.backdated", { count: outcome.backdated }, outcome.backdated) });
    }
    else if (outcome.needsAttention) {
      toast.warning({ text: t("alkokh.offline.needsAttention", { count: outcome.needsAttention }, outcome.needsAttention) });
    }
  }
  finally {
    syncing.value = false;
    await refresh();
  }
}

async function retry(entry: OutboxEntry) {
  await retryEntry(entry.id);
  await syncNow();
}

async function discard(entry: OutboxEntry) {
  // A reason is required, not decorative: this is the only place in the app where
  // a record of money taken can be removed, and the next person needs to know why.
  const reason = window.prompt(t("alkokh.offline.discardReason"));

  if (!reason?.trim())
    return;

  await updateEntry(entry.id, {
    status: "discarded",
    lastError: `${t("alkokh.offline.discardedBy")}: ${reason.trim()}`,
  });

  await refresh();
}

async function exportQueue() {
  try {
    const data = await exportUnsent();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `offline-queue-${localToday()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }
  catch (error: any) {
    toast.error({ text: error?.message ?? t("alkokh.offline.exportFailed") });
  }
}

async function importQueue(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (!file)
    return;

  try {
    const result = await importUnsent(JSON.parse(await file.text()));

    toast.success({ text: t("alkokh.offline.imported", { imported: result.imported, skipped: result.skipped }) });
    await refresh();
  }
  catch (error: any) {
    toast.error({ text: error?.message ?? t("alkokh.offline.importFailed") });
  }
  finally {
    input.value = "";
  }
}

function money(entry: OutboxEntry): string {
  const payload = entry.payload as Record<string, any> | null;
  const amount = Number(payload?.paidAmount ?? 0);

  return amount ? n(amount) : "—";
}

let unsubscribe: (() => void) | null = null;

onMounted(async () => {
  await refresh();
  unsubscribe = subscribeSyncEvents(() => void refresh());
});

onUnmounted(() => unsubscribe?.());
</script>

<template>
  <VCard>
    <VCardItem>
      <VCardTitle>{{ t("alkokh.offline.queueTitle") }}</VCardTitle>
      <template #append>
        <div class="d-flex gap-2 align-center flex-wrap">
          <VChip
            :color="offline.isOffline ? 'warning' : 'success'"
            size="small"
            variant="tonal"
          >
            {{ offline.isOffline ? t("alkokh.offline.offline") : t("alkokh.offline.online") }}
          </VChip>

          <VBtn
            :loading="syncing"
            :disabled="offline.isOffline || !counts.unsent"
            size="small"
            @click="syncNow"
          >
            {{ t("alkokh.offline.syncNow") }}
          </VBtn>

          <VBtn
            size="small"
            variant="tonal"
            @click="exportQueue"
          >
            {{ t("alkokh.offline.exportQueue") }}
          </VBtn>

          <VBtn
            size="small"
            variant="tonal"
            @click="($refs.importInput as HTMLInputElement).click()"
          >
            {{ t("alkokh.offline.importQueue") }}
          </VBtn>

          <input
            ref="importInput"
            type="file"
            accept="application/json"
            hidden
            @change="importQueue"
          >
        </div>
      </template>
    </VCardItem>

    <VCardText>
      <VBtnToggle
        v-model="filter"
        density="compact"
        variant="outlined"
        divided
        mandatory
        class="mb-4"
      >
        <VBtn value="unsent">
          {{ t("alkokh.offline.filterUnsent") }} ({{ counts.unsent }})
        </VBtn>
        <VBtn value="needs_attention">
          {{ t("alkokh.offline.filterNeedsAttention") }} ({{ counts.needsAttention + counts.conflict }})
        </VBtn>
        <VBtn value="synced">
          {{ t("alkokh.offline.filterSynced") }} ({{ counts.synced }})
        </VBtn>
        <VBtn value="all">
          {{ t("alkokh.offline.filterAll") }}
        </VBtn>
      </VBtnToggle>

      <VProgressLinear
        v-if="loading"
        indeterminate
        class="mb-2"
      />

      <VAlert
        v-if="!filtered.length"
        type="success"
        variant="tonal"
        density="compact"
      >
        {{ t("alkokh.offline.queueEmpty") }}
      </VAlert>

      <VTable v-else density="comfortable">
        <thead>
          <tr>
            <th>{{ t("alkokh.offline.colReceipt") }}</th>
            <th>{{ t("alkokh.offline.colCreated") }}</th>
            <th>{{ t("alkokh.offline.colAmount") }}</th>
            <th>{{ t("alkokh.offline.colStatus") }}</th>
            <th>{{ t("alkokh.offline.colDetail") }}</th>
            <th class="text-end">
              {{ t("alkokh.offline.colActions") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entry in filtered"
            :key="entry.id"
          >
            <td class="font-weight-medium">
              {{ entry.receiptNo ?? "—" }}
            </td>
            <td>{{ d(new Date(entry.createdAt), "long") }}</td>
            <td>{{ money(entry) }}</td>
            <td>
              <VChip
                :color="statusColor[entry.status] ?? 'secondary'"
                size="small"
                variant="tonal"
              >
                {{ t(`alkokh.offline.status.${entry.status}`) }}
              </VChip>
            </td>
            <td class="text-medium-emphasis">
              <!-- The server's own words when it refused; the invoice id once it landed. -->
              <span v-if="entry.status === 'synced' && entry.lastError === 'BACKDATE_REFUSED'" class="text-warning">
                {{ t("alkokh.offline.backdatedRow") }}
                <span v-if="(entry.result as any)?.invoice?.id">— {{ (entry.result as any).invoice.id }}</span>
              </span>
              <span v-else-if="(entry.result as any)?.invoice?.id">{{ (entry.result as any).invoice.id }}</span>
              <span v-else-if="entry.lastError">{{ entry.lastError }}</span>
              <span v-else-if="entry.attempts">{{ t("alkokh.offline.attempts", { count: entry.attempts }) }}</span>
              <span v-else>—</span>
            </td>
            <td class="text-end">
              <VBtn
                v-if="entry.status === 'needs_attention' || entry.status === 'conflict'"
                size="small"
                variant="tonal"
                class="me-2"
                @click="retry(entry)"
              >
                {{ t("alkokh.offline.retry") }}
              </VBtn>
              <VBtn
                v-if="canDiscard && entry.status !== 'synced' && entry.status !== 'discarded'"
                size="small"
                color="error"
                variant="text"
                @click="discard(entry)"
              >
                {{ t("alkokh.offline.discard") }}
              </VBtn>
            </td>
          </tr>
        </tbody>
      </VTable>
    </VCardText>
  </VCard>
</template>
